import type { ReactNode } from 'react';

const ROOT_CLASS =
  'flex flex-col gap-6 rounded-card px-6 py-10 transition-colors';

const ROOT_VARIANT = {
  default: 'border border-border-default hover:border-border-hover',
  highlighted: 'bg-surface-frost-02',
} as const;

const CTA_VARIANT = {
  primary: 'site-btn-primary',
  secondary: 'site-btn-secondary',
  outline: 'site-btn-outline',
} as const;

export type PricingPlanCtaVariant = keyof typeof CTA_VARIANT;

export interface PricingPlanCardProps {
  name: string;
  description: string;
  price: string;
  features: readonly string[];
  ctaLabel: string;
  ctaHref: string;
  ctaVariant?: PricingPlanCtaVariant;
  secondaryCtaLabel?: string;
  secondaryCtaHref?: string;
  highlighted?: boolean;
  /** Rendered in the upper-right of the card header (e.g. Plus monthly/annual toggle). */
  headerAction?: ReactNode;
}

export function PricingPlanCard({
  name,
  description,
  price,
  features,
  ctaLabel,
  ctaHref,
  ctaVariant = 'primary',
  secondaryCtaLabel,
  secondaryCtaHref,
  highlighted = false,
  headerAction,
}: PricingPlanCardProps) {
  const rootClass = `${ROOT_CLASS} ${highlighted ? ROOT_VARIANT.highlighted : ROOT_VARIANT.default}`;
  const ctaClass = `${CTA_VARIANT[ctaVariant]} site-btn-md flex-1 min-w-0`;
  const secondaryCtaClass = `${CTA_VARIANT.outline} site-btn-md flex-1 min-w-0`;

  return (
    <div className={rootClass}>
      <div className="flex items-start justify-between gap-4">
        <div className="flex min-w-0 flex-1 flex-col gap-4">
          <p className="text-caption-lg font-medium text-text-primary">{name}</p>
          <p className="text-caption-lg text-text-tertiary">{description}</p>
          <p className="text-display-sm text-text-primary">{price}</p>
        </div>
        {headerAction ? (
          <div className="shrink-0">{headerAction}</div>
        ) : null}
      </div>

      <ul className="flex flex-col gap-3 flex-1">
        {features.map((feature) => (
          <li key={feature} className="text-caption-lg text-text-tertiary">
            {feature}
          </li>
        ))}
      </ul>

      <div className="flex flex-row gap-3">
        <a href={ctaHref} className={ctaClass}>
          {ctaLabel}
        </a>
        {secondaryCtaLabel && secondaryCtaHref ? (
          <a href={secondaryCtaHref} className={secondaryCtaClass}>
            {secondaryCtaLabel}
          </a>
        ) : null}
      </div>
    </div>
  );
}
